import { useState, useEffect } from 'react';
import { Typography, TextField, Box, List, ListItem, ListItemText, Divider, Alert } from '@mui/material';
import { formatCurrency, formatDate } from '../../../utils/formatUtils';
import { buildNotificationRowKey } from '../utils/notificationsUtils';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
} from '../../../common/components';

const NotificationsSendDialog = ({
  open,
  onClose,
  onConfirm,
  selectedEntries = [],
  isSubmitting = false,
}) => {
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!open) {
      setMessage('');
    }
  }, [open]);

  const totalCoupons = selectedEntries.reduce(
    (acc, entry) => acc + (entry.coupons?.length || 0),
    0
  );

  const totalAmount = selectedEntries.reduce((acc, entry) => {
    const headerTotal = Number(entry.header?.total || 0);
    return acc + headerTotal;
  }, 0);

  return (
    <DialogContainer open={open} onClose={onClose} size="medium">
      <DialogTitle>Confirmar envío de recordatorios</DialogTitle>
      <DialogBody sx={{ pt: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Se enviará un correo preventivo a cada suscriptor seleccionado con el detalle de los cupones próximos a vencer y los PDFs adjuntos.
        </Alert>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Resumen del envío
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedEntries.length} suscriptores • {totalCoupons} cupones • {formatCurrency(totalAmount)}
          </Typography>
        </Box>

        <TextField
          label="Mensaje adicional (opcional)"
          multiline
          minRows={3}
          fullWidth
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          placeholder="Ej: Recordá que podés abonar en nuestras oficinas o mediante los medios habituales."
          sx={{ mb: 3 }}
        />

        <Divider sx={{ mb: 2 }} />
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Suscriptores incluidos
        </Typography>

        <List dense sx={{ maxHeight: 260, overflowY: 'auto' }}>
          {selectedEntries.map((entry) => {
            const header = entry.header || {};
            const rowKey = buildNotificationRowKey(header);
            const dueLabel = header.closest_due_date
              ? formatDate(header.closest_due_date)
              : '-';

            return (
              <ListItem key={rowKey} alignItems="flex-start" sx={{ py: 1 }}>
                <ListItemText
                  primary={
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mr: 2 }}>
                        {header.contract_number} • {header.subscriber}
                      </Typography>
                      <Typography variant="subtitle2" color="primary">
                        {formatCurrency(header.total || 0)}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" color="text.secondary">
                        Email: {header.subscriber_email || 'Sin email'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Cupones: {entry.coupons?.length || 0} • Próximo vencimiento: {dueLabel}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      </DialogBody>
      <DialogActions
        handleClose={onClose}
        handleSave={() => onConfirm(message)}
        saveLabel="Enviar recordatorios"
        savingLabel="Enviando…"
        saveButtonProps={{
          variant: 'danger',
          isLoading: isSubmitting,
          disabled: isSubmitting || selectedEntries.length === 0,
        }}
        cancelButtonProps={{
          disabled: isSubmitting,
        }}
      />
    </DialogContainer>
  );
};

export default NotificationsSendDialog;
